/**
 * The shared forms API: one ordinary definition, typed values, typed layout.
 *
 * Values are typed by kind and live in one flat owner. Editing text a control
 * holds is separate from the value it publishes, and text that does not read
 * blocks readiness rather than leaving the previous value submit-ready.
 */
import * as React from "react";

export type FieldKind =
    | "text"
    | "password"
    | "email"
    | "textarea"
    | "markdown"
    | "select"
    | "boolean"
    | "number"
    | "decimal"
    | "date"
    | "datetime"
    | "duration"
    | "energy"
    | "length"
    | "weight"
    | "percent"
    | "multiplier"
    | "range";

/**
 * A compound date widget: the POST name of each segment, and the choices the
 * widget offers, so a server that restricts the years keeps that restriction.
 */
export type FieldParts = {
    readonly year: string;
    readonly month: string;
    readonly day: string;
    /** The segments in the locale's order: ["month", "day", "year"] for en-us. */
    readonly order: readonly ("year" | "month" | "day")[];
    /** In the widget's order, newest first when it says so. */
    readonly years: readonly number[];
    /** Twelve labels, January first. */
    readonly months: readonly string[];
    /** The blank option's label per segment: year, month, day. */
    readonly empty: readonly string[];
};

export type FieldDefinition<TValue = unknown> = {
    readonly kind: FieldKind;
    readonly name?: string;
    readonly label?: string;
    readonly helpText?: string | null;
    readonly placeholder?: string | null;
    readonly required?: boolean;
    readonly readOnly?: boolean;
    readonly nullable?: boolean;
    readonly widget?: string | null;
    readonly options?: readonly (readonly [TValue, string])[] | null;
    /** Members the control shows but refuses. Presentation, not the domain. */
    readonly disabledOptions?: readonly TValue[];
    readonly htmlName?: string;
    readonly parts?: FieldParts | null;
    /** The pre-rename spelling of `kind`, still emitted for other consumers. */
    readonly type?: string;
};

export type FormDefinition<
    TFields extends Readonly<Record<string, FieldDefinition<unknown>>> = Readonly<
        Record<string, FieldDefinition>
    >,
> = {
    readonly fields: TFields;
    readonly defaults?: Readonly<Record<string, unknown>>;
    readonly iterator?: readonly string[];
    /** Raw held text, per field. Whole `null` is an unbound form: no drafts. */
    readonly drafts?: Readonly<
        Partial<Record<keyof TFields & string, string | boolean | null>>
    > | null;
    readonly errors?: Readonly<Record<string, readonly string[]>> | null;
    readonly nonFieldErrors?: readonly string[] | null;
    readonly hidden_fields?: readonly string[];
    readonly prefix?: string;
    readonly name?: string;
};

// A Django definition states every field entry optional, because a view may
// delete one; a generated `@form` const states them all. Both are accepted
// structurally rather than through `FormDefinition`, whose index signature an
// optional entry cannot satisfy.
type AnyDefinition = {readonly fields: object};

// A definition, or the bare field map a generic consumer parameterises over.
type FieldsOf<TDefinition> = TDefinition extends {readonly fields: infer TFields}
    ? TFields
    : TDefinition;

export type FieldName<TDefinition> = keyof FieldsOf<TDefinition> & string;

type FieldOf<TDefinition, TName extends FieldName<TDefinition>> = NonNullable<
    FieldsOf<TDefinition>[TName]
>;

type BaseValue<TKind> = TKind extends
    | "text"
    | "password"
    | "email"
    | "textarea"
    | "markdown"
    | "select"
    | "date"
    | "datetime"
    | "decimal"
    | "energy"
    | "length"
    | "weight"
    | "percent"
    | "multiplier"
    | "range"
    ? string
    : TKind extends "number" | "duration"
      ? number
      : TKind extends "boolean"
        ? boolean
        : never;

type NullOf<TField> = TField extends {readonly nullable: true} ? null : never;

// A stated domain IS the value type. `options: null` and an absent key are not
// a domain, so they fall through to the kind.
type OptionValue<TField> = TField extends {
    readonly options: readonly (readonly [infer TValue, unknown])[];
}
    ? TValue
    : never;

// A control that can be emptied always can, so a live value carries a blank a
// submitted one does not. Clearability belongs to the control, hence the kind;
// `nullable` states the submission. Neither follows from `required`.
type ClearableKind =
    | "decimal"
    | "number"
    | "date"
    | "datetime"
    | "duration"
    | "energy"
    | "length"
    | "weight"
    | "percent"
    | "multiplier"
    | "range";

// A select is clearable too, and for a reason the other kinds do not share: a
// form nobody has answered starts unselected, and no first option is chosen on
// its behalf. That blank is live only — `SubmissionFields` still refuses it
// unless the domain states a null member or the field is `nullable`.
type LiveNullOf<TField> = TField extends {readonly kind: ClearableKind | "select"}
    ? null
    : never;

type SubmittedValue<TField> = [OptionValue<TField>] extends [never]
    ? TField extends {readonly kind: infer TKind}
        ? BaseValue<TKind> | NullOf<TField>
        : never
    : OptionValue<TField> | NullOf<TField>;

type ValueOfField<TField> = SubmittedValue<TField> | LiveNullOf<TField>;

/** The values a form holds while it is edited. */
export type ValueFields<TDefinition> = {
    [TName in FieldName<TDefinition>]-?: ValueOfField<FieldOf<TDefinition, TName>>;
};

/**
 * The values a ready form submits. A cleared field is not ready and never
 * reaches here; a blank that is a domain MEMBER is a choice and stays.
 */
export type SubmissionFields<TDefinition> = {
    [TName in FieldName<TDefinition>]-?: SubmittedValue<FieldOf<TDefinition, TName>>;
};

export type ValueOfDefinition<TField> = ValueOfField<TField>;

/**
 * Cached local readiness and the submission projection. It says nothing about
 * server acceptance. The failure arm names what holds the form back, per
 * field, in the words a submit attempt shows beside the control.
 */
export type Validated<TData> =
    | {readonly success: true; readonly data: TData}
    | {
          readonly success: false;
          readonly errors: Readonly<Record<string, readonly string[]>>;
      };

export type FieldErrors = readonly string[] | null;

/**
 * One error as an RPC reply states it, before it is bound to a field. A list
 * index inside `loc` is a number, as the validator writes it.
 */
export type ReplyError = {
    readonly loc: readonly (string | number)[];
    readonly msg: string;
};

/**
 * Errors as they arrive from a reply, before they are bound to fields. A bare
 * string is a message that names no field: the server sends those whenever a
 * handler asserts, and the two shapes share one array.
 */
export type ServerErrors =
    | Readonly<Record<string, readonly string[]>>
    | readonly (ReplyError | string)[]
    | string;

export type SetErrorsOptions = {
    /** The revision the errors were computed against. A later edit discards them. */
    readonly revision?: number;
    readonly prefix?: readonly string[];
};

export type FieldHandle<
    TKind extends FieldKind = FieldKind,
    TValue = BaseValue<TKind> | null,
> = {
    readonly kind: TKind;
    readonly name: string;
    readonly htmlName: string;
    readonly label: string;
    readonly helpText: string | null;
    readonly placeholder?: string;
    readonly required: boolean;
    readonly readOnly: boolean;
    readonly widget: string | null;
    readonly parts: FieldParts | null;
    readonly options: readonly (readonly [TValue, string])[];
    readonly disabledOptions: readonly TValue[];
    readonly value: TValue;
    // A plain function property, so the setter is contravariant: a control
    // declared over a wider domain cannot be handed a narrower field and write
    // a value outside it. Controls are generic over the value instead.
    readonly onChange: (value: TValue) => void;
    readonly errors: FieldErrors;
    readonly validated: Validated<TValue>;
    readonly revision: number;
    setServerErrors(errors: ServerErrors, options?: SetErrorsOptions): void;
};

/** Every value a field can hold. A domain may be numbers or booleans, not only text. */
export type AnyValue = string | number | boolean | null;

/**
 * The values a kind admits. A select carries whatever its domain states —
 * strings, numbers, booleans or a blank — so its control stays generic over
 * that domain rather than assuming text.
 */
export type AdmissibleValue<TKind extends FieldKind> = TKind extends "select"
    ? AnyValue
    : BaseValue<TKind> | null;

/**
 * What a control of a kind can WRITE, which is not what it can read. A
 * clearable control can always be emptied, so the blank is one of its outputs;
 * a text or boolean control has no keystroke that produces one, whatever the
 * field's nullability. `select` is absent on purpose: a select writes members
 * of a domain only the field states, so its control stays generic over it.
 */
export type WritableValue<TKind extends Exclude<FieldKind, "select">> =
    TKind extends ClearableKind ? BaseValue<TKind> | null : BaseValue<TKind>;

/**
 * The handle a REGISTERED control receives. One entry serves every field of its
 * kind, so it reads whatever the kind admits — a nullable text field is still a
 * text field — while its setter is narrowed to what the kind's control can
 * produce. Without that narrowing a registered text or boolean control could
 * write a blank the field's own setter refuses, which is the same unsoundness
 * the contravariant setter exists to prevent on the direct component path.
 */
export type RegisteredFieldHandle<TKind extends Exclude<FieldKind, "select">> = Omit<
    FieldHandle<TKind, AdmissibleValue<TKind>>,
    "onChange"
> & {
    readonly onChange: (value: WritableValue<TKind>) => void;
};

/**
 * A handle of SOME kind and SOME value, without its setter. A union over every
 * value type can hold the setter only by making it bivariant, which is exactly
 * how a control ends up writing null into a field that has no null. Anything
 * that WRITES takes the handle generically instead.
 */
export type AnyFieldHandle = {
    [TKind in FieldKind]: Omit<FieldHandle<TKind, AdmissibleValue<TKind>>, "onChange">;
}[FieldKind];

// `-?` is deliberate: a Django view may delete a field at runtime, which the
// definition states as an optional entry, but that is not permission to read
// an undeclared name off the handle.
type FieldHandles<TDefinition, TValues> = {
    readonly [TName in FieldName<TDefinition>]-?: FieldHandle<
        FieldOf<TDefinition, TName> extends {
            readonly kind: infer TKind extends FieldKind;
        }
            ? TKind
            : never,
        TName extends keyof TValues
            ? TValues[TName]
            : ValueOfField<FieldOf<TDefinition, TName>>
    >;
};

export type FormHandle<TDefinition, TValues = ValueFields<TDefinition>> = {
    readonly definition: TDefinition;
    readonly values: TValues;
    /** The baseline `reset` returns to, and what a dirty check compares against. */
    readonly initial: TValues;
    /** The field names in declaration order, which is the order a form renders. */
    readonly order: readonly FieldName<TDefinition>[];
    readonly fields: FieldHandles<TDefinition, TValues>;
    readonly validated: Validated<SubmissionFields<TDefinition>>;
    readonly errors: Readonly<Record<FieldName<TDefinition>, FieldErrors>>;
    readonly nonFieldErrors: readonly string[] | null;
    readonly revision: number;
    update(next: TValues | ((previous: TValues) => TValues)): void;
    reset(): void;
    setServerErrors(errors: ServerErrors, options?: SetErrorsOptions): void;
};

export type OwnedFormOptions<TDefinition> = {
    /** Whole `null` means no overrides; `{field: null}` is a field's own null. */
    readonly initial?: Partial<ValueFields<TDefinition>> | null;
    /**
     * Raw text a field is holding, in the same vocabulary a definition carries
     * it, so a bound Django form's own `drafts` forwards unchanged. Text that
     * reads as a value parses to one; text that cannot be read — a stored
     * choice that is no longer a member, a half-typed number — leaves the field
     * unready, which is what keeps an unreadable input out of a payload.
     *
     * Whole `null` means no drafts; `{field: null}` is a field's own null, as
     * with `initial`.
     */
    readonly drafts?: Partial<
        Record<FieldName<TDefinition>, string | boolean | null>
    > | null;
    // Stated, not merely absent: excess-property checking does not survive a
    // variable, so an options object built elsewhere would otherwise fall back
    // to this overload and own its state while the caller believed it was
    // controlled.
    readonly value?: never;
    readonly onChange?: never;
};

export type ControlledFormOptions<TValues> = {
    readonly value: TValues;
    readonly onChange: (value: TValues) => void;
    /** Presentation for every field at once. It does not change the payload. */
    readonly readOnly?: boolean;
    readonly initial?: never;
};

// ---------------------------------------------------------------------------
// State. One owner, flat values, no effects: the initial values AND the
// initial errors stand on the first render, including SSR.
// ---------------------------------------------------------------------------

type RuntimeField = FieldDefinition<AnyValue>;
type RuntimeFields = Readonly<Record<string, RuntimeField>>;
type RuntimeValues = Readonly<Record<string, unknown>>;
/** Raw text a control holds that does not read as a value, per field. */
type RuntimeDrafts = Readonly<Record<string, string>>;

type ErrorState = {
    readonly fields: Readonly<Record<string, readonly string[] | null>>;
    readonly nonField: readonly string[] | null;
};

const TEXT_KINDS: readonly string[] = [
    "text",
    "password",
    "email",
    "textarea",
    "markdown",
];

const DECIMAL_KINDS: readonly string[] = [
    "decimal",
    "energy",
    "length",
    "weight",
    "percent",
    "multiplier",
    "range",
];

const NUMERAL = /^-?\d*\.?\d*$/;

/**
 * The one channel between a control and the form: the raw text the control is
 * holding, and the form's ear for text that does not read. It rides on the
 * handle under a symbol so a spread carries it and no public prop states it.
 */
const bindingKey = Symbol.for("@gesamtkunstwerk/shared/forms.binding");

type FieldBinding = {
    /** Text the form was handed for this field which did not read. */
    readonly draft: string | null;
    /**
     * Bumped when this field's value is stated from OUTSIDE its control — a
     * programmatic `onChange`, an `update` that moves it, a `reset`. A control's
     * editing text survives its own publishes, which is what keeps "1." on
     * screen, and is discarded here, where the value it described is gone.
     */
    readonly epoch: number;
    readonly report: (draft: string | null) => void;
    /**
     * The control's own publish. It is the same write as `onChange` without the
     * restatement, and telling the two apart is the whole reason it exists: a
     * control must not clear the buffer it is itself typing into.
     */
    readonly publish: (value: AnyValue) => void;
};

type Bound = {readonly [bindingKey]?: FieldBinding};

function bindingOf(field: object): FieldBinding | null {
    return (field as Bound)[bindingKey] ?? null;
}

function runtimeFields(definition: AnyDefinition): RuntimeFields {
    // `AnyDefinition` states only that `fields` is an object, and a Django
    // view may delete an entry, so the map that rendered is the one to read.
    const declared = definition.fields as Readonly<
        Record<string, RuntimeField | undefined>
    >;
    const present: Record<string, RuntimeField> = {};
    for (const name of Object.keys(declared)) {
        const field = declared[name];
        if (field !== undefined) {
            present[name] = field;
        }
    }
    return present;
}

type DefinitionExtras = {
    readonly defaults?: Readonly<Record<string, unknown>>;
    readonly drafts?: Readonly<Record<string, string | boolean | null>> | null;
    readonly errors?: Readonly<Record<string, readonly string[]>> | null;
    readonly nonFieldErrors?: readonly string[] | null;
    readonly hidden_fields?: readonly string[];
};

function extrasOf(definition: AnyDefinition): DefinitionExtras {
    return definition as DefinitionExtras;
}

function optionText(value: AnyValue): string {
    return value == null ? "" : String(value);
}

function blankOf(field: RuntimeField): AnyValue {
    if (TEXT_KINDS.includes(field.kind)) {
        return "";
    }
    if (field.kind === "boolean") {
        return false;
    }
    // Never the first option: a choice nobody made is not a value.
    return null;
}

/** Blank text: nothing but whitespace. The server's `is_blank` decides; this
 * agrees with it except on a few control and format characters. */
function isBlank(value: unknown): boolean {
    return typeof value === "string" && value.trim() === "";
}

/** Null is a value only where the field says so: `nullable`, or a null member. */
function admitsNull(field: RuntimeField): boolean {
    return (
        field.nullable === true ||
        (field.options ?? []).some(([value]) => value === null)
    );
}

/**
 * Text as the field's own value, or null when it cannot be read at all. Text
 * that reads parses — which is how a bound Django POST round-trips — and a
 * decimal keeps the text it was typed as, because "1." is a decimal and is
 * not a number.
 */
function readText(
    field: RuntimeField,
    text: string,
): {readonly value: AnyValue} | null {
    const kind = field.kind;
    if (TEXT_KINDS.includes(kind)) {
        return {value: text};
    }
    if (kind === "select") {
        const member = (field.options ?? []).find(
            ([value]) => optionText(value) === text,
        );
        if (member !== undefined) {
            return {value: member[0]};
        }
        return text === "" ? {value: null} : null;
    }
    if (kind === "boolean") {
        return {value: text === "true" || text === "on" || text === "True"};
    }
    if (text === "") {
        return {value: null};
    }
    if (kind === "number") {
        return /^-?\d+$/.test(text) ? {value: Number(text)} : null;
    }
    if (kind === "duration") {
        return /^-?\d+(\.\d+)?$/.test(text) ? {value: Number(text)} : null;
    }
    if (DECIMAL_KINDS.includes(kind)) {
        return NUMERAL.test(text) && /\d/.test(text) ? {value: text} : null;
    }
    if (kind === "date") {
        return /^\d{4}-\d{2}-\d{2}$/.test(text) ? {value: text} : null;
    }
    if (kind === "datetime") {
        return /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(text) ? {value: text} : null;
    }
    return {value: text};
}

type SeedOptions = {
    readonly value?: RuntimeValues;
    readonly onChange?: (value: RuntimeValues) => void;
    readonly initial?: Readonly<Record<string, unknown>> | null;
    readonly drafts?: Readonly<Record<string, string | boolean | null>> | null;
    readonly readOnly?: boolean;
};

type Seed = {
    readonly values: RuntimeValues;
    readonly drafts: RuntimeDrafts;
    readonly errors: ErrorState;
};

function seedForm(
    fields: RuntimeFields,
    definition: AnyDefinition,
    options: SeedOptions,
): Seed {
    const extras = extrasOf(definition);
    const names = Object.keys(fields);
    const controlled = options.value !== undefined;
    const values: Record<string, unknown> = {...options.value};

    if (!controlled) {
        const defaults = extras.defaults;
        for (const name of names) {
            values[name] =
                defaults !== undefined && name in defaults
                    ? defaults[name]
                    : blankOf(fields[name]);
        }
        const initial = options.initial;
        // Whole `null` is no overrides; `{field: null}` is that field's own null.
        if (initial != null) {
            for (const name of names) {
                if (name in initial) {
                    values[name] = initial[name];
                }
            }
        }
    }

    const drafts: Record<string, string> = {};
    const held = {...extras.drafts, ...options.drafts};
    for (const name of names) {
        if (!(name in held)) {
            continue;
        }
        const draft = held[name];
        if (draft === null) {
            continue;
        }
        const read =
            typeof draft === "boolean" ? {value: draft} : readText(fields[name], draft);
        if (read === null) {
            drafts[name] = draft as string;
        } else if (!controlled) {
            values[name] = read.value;
        }
    }

    return {
        values,
        drafts,
        errors: {
            fields: extras.errors ?? {},
            nonField: extras.nonFieldErrors ?? null,
        },
    };
}

/** What a submit attempt says beside a field holding text nobody can read. */
const UNREADABLE: Readonly<Record<FieldKind, string>> = {
    text: "Enter a valid value.",
    password: "Enter a valid value.",
    email: "Enter a valid value.",
    textarea: "Enter a valid value.",
    markdown: "Enter a valid value.",
    select: "Choose an option.",
    boolean: "Enter a valid value.",
    number: "Enter a number.",
    decimal: "Enter a number.",
    energy: "Enter a number.",
    length: "Enter a number.",
    weight: "Enter a number.",
    percent: "Enter a number.",
    multiplier: "Enter a number.",
    range: "Enter a number.",
    duration: "Enter a duration.",
    date: "Enter a date.",
    datetime: "Enter a date.",
};

/**
 * Whether one field is ready: no unreadable text held, and a value the field
 * admits. Text nobody can read, a null the domain does not state, and blank
 * text in a required field that cannot be null are held back. A nullable
 * text field's blank is sent as null, and the server judges it.
 */
function blockedBy(
    field: RuntimeField,
    value: unknown,
    draft: string | null,
): string | null {
    if (draft !== null) {
        return UNREADABLE[field.kind];
    }
    if (value === null && !admitsNull(field)) {
        return "This field is required.";
    }
    if (
        TEXT_KINDS.includes(field.kind) &&
        field.required === true &&
        !admitsNull(field) &&
        isBlank(value)
    ) {
        return "This field is required.";
    }
    return null;
}

/**
 * The submission projection: the declared fields that are present, and only
 * those. The null a clearable control can always produce is not ready to send
 * unless the field's own domain states it. Blank text in a nullable field is
 * sent as null; the draft keeps what was typed.
 */
function projectValues(
    fields: RuntimeFields,
    values: RuntimeValues,
    drafts: RuntimeDrafts,
): Validated<Readonly<Record<string, unknown>>> {
    const data: Record<string, unknown> = {};
    const errors: Record<string, readonly string[]> = {};
    for (const name of Object.keys(fields)) {
        const blocked = blockedBy(fields[name], values[name], drafts[name] ?? null);
        if (blocked !== null) {
            errors[name] = [blocked];
        }
        data[name] =
            TEXT_KINDS.includes(fields[name].kind) &&
            admitsNull(fields[name]) &&
            isBlank(values[name])
                ? null
                : values[name];
    }
    return Object.keys(errors).length > 0
        ? {success: false, errors}
        : {success: true, data};
}

function isReplyErrors(
    errors: ServerErrors,
): errors is readonly (ReplyError | string)[] {
    return Array.isArray(errors);
}

function normalizeErrors(
    incoming: ServerErrors,
    names: readonly string[],
    prefix: readonly string[] | undefined,
): ErrorState {
    const fields: Record<string, string[]> = {};
    const nonField: string[] = [];

    const bind = (name: string | null, message: string) => {
        if (name !== null && names.includes(name)) {
            const held = fields[name] ?? [];
            held.push(message);
            fields[name] = held;
        } else {
            // An error that binds to no field is still an error.
            nonField.push(message);
        }
    };

    if (typeof incoming === "string") {
        nonField.push(incoming);
    } else if (isReplyErrors(incoming)) {
        for (const entry of incoming) {
            if (typeof entry === "string") {
                nonField.push(entry);
                continue;
            }
            const loc = entry.loc;
            const rooted =
                prefix === undefined
                    ? loc
                    : prefix.every((segment, index) => loc[index] === segment)
                      ? loc.slice(prefix.length)
                      : null;
            // A numeric head is a list index, which names no field of this form.
            const head = rooted?.[0];
            bind(typeof head === "string" ? head : null, entry.msg);
        }
    } else {
        for (const [name, messages] of Object.entries(incoming)) {
            for (const message of messages) {
                bind(name, message);
            }
        }
    }

    return {fields, nonField: nonField.length > 0 ? nonField : null};
}

type Owner = {
    readonly values: RuntimeValues;
    readonly initial: RuntimeValues;
    readonly drafts: RuntimeDrafts;
    readonly errors: ErrorState;
    readonly revision: number;
    /** Per field. Bumped where the value was stated from outside its control. */
    readonly epochs: Readonly<Record<string, number>>;
    /** A value stated from outside the field's own control. */
    readonly write: (name: string, value: unknown) => void;
    /** The field's own control, publishing text it has just read. */
    readonly publish: (name: string, value: unknown) => void;
    /** Every value at once; only the ones that moved count as stated. */
    readonly update: (
        next: RuntimeValues | ((previous: RuntimeValues) => RuntimeValues),
    ) => void;
    readonly reset: () => void;
    readonly report: (name: string, draft: string | null) => void;
    readonly setErrors: (
        incoming: ServerErrors,
        options: SetErrorsOptions | undefined,
        names: readonly string[],
    ) => void;
};

function useOwner(
    fields: RuntimeFields,
    definition: AnyDefinition,
    options: SeedOptions,
): Owner {
    const [seed] = React.useState(() => seedForm(fields, definition, options));
    const [owned, setOwned] = React.useState<RuntimeValues>(seed.values);
    const [drafts, setDrafts] = React.useState<RuntimeDrafts>(seed.drafts);
    const [errors, setErrors] = React.useState<ErrorState>(seed.errors);
    const [revision, setRevision] = React.useState(0);
    const [epochs, setEpochs] = React.useState<Readonly<Record<string, number>>>({});

    const values = options.value ?? owned;
    const onChange = options.onChange;
    // setServerErrors is called from a callback that closed over an older
    // render, so the revision it compares against is the current one.
    const current = React.useRef(revision);
    current.current = revision;

    // Writes in one tick accumulate here, so two of them do not both compute
    // from the value this render was handed and lose the first. Every commit
    // drops the accumulator: for a controlled form the parent's own value is
    // authoritative and may reject or transform anything this form published,
    // and for an owned one the committed state is.
    const pending = React.useRef<RuntimeValues | null>(null);
    React.useEffect(() => {
        pending.current = null;
    });
    const live = React.useRef(values);
    live.current = values;
    const latest = () => pending.current ?? live.current;

    // What the form last asked its owner for; a value that arrives as asked is
    // the form's own, whatever else the owner changed in the same commit.
    const asked = React.useRef<RuntimeValues | null>(null);
    const apply = (mutate: (previous: RuntimeValues) => RuntimeValues) => {
        setRevision((previous) => previous + 1);
        const next = mutate(latest());
        pending.current = next;
        asked.current = next;
        if (onChange !== undefined) {
            onChange(next);
        } else {
            setOwned(next);
        }
    };

    // A value stated from outside a control replaces whatever that control was
    // holding — the unreadable text included, so what is on screen and what the
    // form will send cannot disagree.
    const restate = (names: readonly string[]) => {
        if (names.length === 0) {
            return;
        }
        setEpochs((previous) => {
            const next = {...previous};
            for (const name of names) {
                next[name] = (previous[name] ?? 0) + 1;
            }
            return next;
        });
        setDrafts((previous) => {
            const kept: Record<string, string> = {};
            let dropped = false;
            for (const name of Object.keys(previous)) {
                if (names.includes(name)) {
                    dropped = true;
                } else {
                    kept[name] = previous[name];
                }
            }
            return dropped ? kept : previous;
        });
    };

    // An error describes the value it was raised against; editing that field
    // retires it, and the next submit says what it thinks of the new one.
    const forgive = (names: readonly string[]) => {
        setErrors((previous) => {
            if (!names.some((name) => previous.fields[name] != null)) {
                return previous;
            }
            const kept: Record<string, readonly string[] | null> = {};
            for (const [name, held] of Object.entries(previous.fields)) {
                if (!names.includes(name)) {
                    kept[name] = held;
                }
            }
            return {...previous, fields: kept};
        });
    };

    // A controlled parent may replace a value on its own (a shortcut, a reload).
    // That is a statement from outside the control too, so the text the control
    // was holding goes with it; the form's own writes are recognised by the
    // value they asked for and keep their text.
    const seen = React.useRef(values);
    React.useEffect(() => {
        const previous = seen.current;
        seen.current = values;
        if (previous === values || options.value === undefined) {
            return;
        }
        const own = asked.current;
        restate(
            Object.keys(fields).filter(
                (name) =>
                    !Object.is(previous[name], values[name]) &&
                    !(own !== null && Object.is(own[name], values[name])),
            ),
        );
    });

    return {
        values,
        initial: seed.values,
        drafts,
        errors,
        revision,
        epochs,
        write: (name, value) => {
            restate([name]);
            forgive([name]);
            apply((previous) => ({...previous, [name]: value}));
        },
        publish: (name, value) => {
            // No restate: the control that typed this owns its own text, which
            // is how "1." stays on screen after publishing 1.
            forgive([name]);
            apply((previous) => ({...previous, [name]: value}));
        },
        update: (next) => {
            const base = latest();
            const resolved = typeof next === "function" ? next(base) : next;
            // Restating a value identically is not stating it anew: a field
            // nobody moved keeps the text it was holding, unreadable or not.
            const moved = Object.keys(fields).filter(
                (name) => !Object.is(base[name], resolved[name]),
            );
            restate(moved);
            forgive(moved);
            apply(() => resolved);
        },
        reset: () => {
            restate(Object.keys(fields));
            setErrors({fields: {}, nonField: null});
            apply(() => seed.values);
        },
        report: (name, draft) => {
            setDrafts((previous) => {
                const held = previous[name] ?? null;
                if (held === draft) {
                    return previous;
                }
                if (draft === null) {
                    const {[name]: _dropped, ...rest} = previous;
                    return rest;
                }
                return {...previous, [name]: draft};
            });
        },
        setErrors: (incoming, setOptions, names) => {
            if (
                setOptions?.revision != null &&
                setOptions.revision !== current.current
            ) {
                return;
            }
            setErrors(normalizeErrors(incoming, names, setOptions?.prefix));
        },
    };
}

type HandleInputs = {
    readonly field: RuntimeField;
    readonly key: string;
    readonly value: unknown;
    readonly draft: string | null;
    readonly errors: FieldErrors;
    readonly revision: number;
    readonly epoch: number;
    readonly readOnly: boolean | undefined;
    readonly onChange: (value: AnyValue) => void;
    readonly report: (draft: string | null) => void;
    readonly publish: (value: AnyValue) => void;
    readonly setServerErrors: (
        errors: ServerErrors,
        options?: SetErrorsOptions,
    ) => void;
};

function fieldHandle(inputs: HandleInputs): FieldHandle<FieldKind, AnyValue> & Bound {
    const {field} = inputs;
    // The POST name. A prefixed Django field posts its html name, and a
    // hand-composed native form reads this off the handle.
    const htmlName = field.htmlName ?? field.name ?? inputs.key;
    const value = inputs.value as AnyValue;
    const blocked = blockedBy(field, value, inputs.draft);

    return {
        kind: field.kind,
        name: htmlName,
        htmlName,
        label: field.label ?? inputs.key,
        helpText: field.helpText ?? null,
        placeholder: field.placeholder ?? undefined,
        required: field.required ?? false,
        readOnly: inputs.readOnly ?? field.readOnly ?? false,
        widget: field.widget ?? null,
        parts: field.parts ?? null,
        options: field.options ?? [],
        disabledOptions: field.disabledOptions ?? [],
        value,
        onChange: inputs.onChange,
        errors: inputs.errors,
        validated:
            blocked === null
                ? {success: true, data: value}
                : {success: false, errors: {[inputs.key]: [blocked]}},
        revision: inputs.revision,
        setServerErrors: inputs.setServerErrors,
        [bindingKey]: {
            draft: inputs.draft,
            epoch: inputs.epoch,
            report: inputs.report,
            publish: inputs.publish,
        },
    };
}

export function useForm<
    const TDefinition extends AnyDefinition,
    TValues extends ValueFields<TDefinition>,
>(
    definition: TDefinition,
    options: ControlledFormOptions<TValues>,
): FormHandle<TDefinition, TValues>;
export function useForm<const TDefinition extends AnyDefinition>(
    definition: TDefinition,
    options?: OwnedFormOptions<TDefinition>,
): FormHandle<TDefinition>;
export function useForm<const TDefinition extends AnyDefinition>(
    definition: TDefinition,
    options?: unknown,
): FormHandle<TDefinition, ValueFields<TDefinition>> {
    const settings = (options ?? {}) as SeedOptions;
    const fields = runtimeFields(definition);
    const names = Object.keys(fields);
    const owner = useOwner(fields, definition, settings);
    const {values, drafts} = owner;

    const validated = React.useMemo(
        () => projectValues(fields, values, drafts),
        // `fields` is rebuilt every render; the definition is what moves.
        [definition, values, drafts],
    );

    const setServerErrors = (incoming: ServerErrors, setOptions?: SetErrorsOptions) => {
        owner.setErrors(incoming, setOptions, names);
    };

    const handles: Record<string, unknown> = {};
    const errors: Record<string, FieldErrors> = {};
    for (const key of names) {
        errors[key] = owner.errors.fields[key] ?? null;
        handles[key] = fieldHandle({
            field: fields[key],
            key,
            value: values[key],
            draft: drafts[key] ?? null,
            errors: errors[key],
            revision: owner.revision,
            epoch: owner.epochs[key] ?? 0,
            readOnly: settings.readOnly,
            onChange: (value) => {
                owner.write(key, value);
            },
            report: (draft) => {
                owner.report(key, draft);
            },
            publish: (value) => {
                owner.publish(key, value);
            },
            setServerErrors,
        });
    }

    const handle = {
        definition,
        values,
        initial: owner.initial,
        order: names,
        fields: handles,
        validated,
        errors,
        nonFieldErrors: owner.errors.nonField,
        revision: owner.revision,
        update: (next: unknown) => {
            owner.update(
                next as RuntimeValues | ((previous: RuntimeValues) => RuntimeValues),
            );
        },
        reset: owner.reset,
        setServerErrors,
    };

    // The one assertion the mapped handle types need: no object a loop builds
    // can satisfy `FieldHandles` structurally.
    return handle as unknown as FormHandle<TDefinition, ValueFields<TDefinition>>;
}

export type OwnedFieldOptions<TValue> = {
    readonly initial?: TValue | null;
    readonly value?: never;
    readonly onChange?: never;
};

// Generic over the caller's own value type, exactly as the controlled `useForm`
// overload is: a parent holding a chosen member states a narrower domain than
// the field's live one, and the handle it gets back refuses the blank its state
// has no room for.
export function useField<
    const TDefinition extends FieldDefinition,
    TValue extends ValueOfField<TDefinition>,
>(
    definition: TDefinition,
    options: ControlledFormOptions<TValue>,
): FieldHandle<TDefinition["kind"], TValue>;
export function useField<const TDefinition extends FieldDefinition>(
    definition: TDefinition,
    options?: OwnedFieldOptions<ValueOfField<TDefinition>>,
): FieldHandle<TDefinition["kind"], ValueOfField<TDefinition>>;
export function useField<const TDefinition extends FieldDefinition>(
    definition: TDefinition,
    options?: unknown,
): FieldHandle<TDefinition["kind"], ValueOfField<TDefinition>> {
    const settings = (options ?? {}) as {
        readonly value?: AnyValue;
        readonly onChange?: (value: AnyValue) => void;
        readonly initial?: AnyValue;
        readonly readOnly?: boolean;
    };
    const field = definition as RuntimeField;
    const key = field.name ?? "";
    const fields: RuntimeFields = {[key]: field};
    const controlled = settings.onChange !== undefined;

    const owner = useOwner(
        fields,
        {fields},
        {
            value: controlled ? {[key]: settings.value ?? null} : undefined,
            onChange: controlled
                ? (next) => {
                      settings.onChange?.(next[key] as AnyValue);
                  }
                : undefined,
            initial: "initial" in settings ? {[key]: settings.initial} : undefined,
            readOnly: settings.readOnly,
        },
    );

    // A standalone field has nowhere to put a non-field error, so every
    // message it is handed is its own.
    const errors = [
        ...(owner.errors.fields[key] ?? []),
        ...(owner.errors.nonField ?? []),
    ];

    const handle = fieldHandle({
        field,
        key,
        value: owner.values[key],
        draft: owner.drafts[key] ?? null,
        errors: errors.length > 0 ? errors : null,
        revision: owner.revision,
        epoch: owner.epochs[key] ?? 0,
        readOnly: settings.readOnly,
        onChange: (value) => {
            owner.write(key, value);
        },
        report: (draft) => {
            owner.report(key, draft);
        },
        publish: (value) => {
            owner.publish(key, value);
        },
        setServerErrors: (incoming, setOptions) => {
            owner.setErrors(incoming, setOptions, [key]);
        },
    });

    return handle as unknown as FieldHandle<
        TDefinition["kind"],
        ValueOfField<TDefinition>
    >;
}

export type SubmitOptions = {
    /** Where the reply's errors sit relative to this form's fields. */
    readonly prefix?: readonly string[];
};

/** The whole of a form the helper touches: read readiness, then write errors. */
export type SubmittableForm<TValues> = {
    readonly validated: Validated<TValues>;
    readonly revision: number;
    setServerErrors(errors: ServerErrors, options?: SetErrorsOptions): void;
};

/** The reply arm the helper can bind. Every other arm is returned untouched. */
export type InvalidReply = {
    readonly type: "invalid";
    readonly errors: readonly (ReplyError | string)[];
};

// A reply whose "invalid" arm is not {loc, msg} | string entries cannot be
// bound, and saying so here beats discovering it when the errors silently go
// nowhere.
export type BindableReply<TResponse> = [Extract<TResponse, {type: "invalid"}>] extends [
    never,
]
    ? unknown
    : Extract<TResponse, {type: "invalid"}> extends InvalidReply
      ? unknown
      : {
            readonly "submitForm error: the reply's invalid arm must carry ({loc, msg} | string)[]": never;
        };

/**
 * Reads cached validated data once, captures the revision, binds the reply's
 * errors against that snapshot, and returns the reply the caller's RPC
 * returned. Returns null WITHOUT sending when the form is not ready, which is
 * not a failure reply: the caller still clears its own busy state. What held
 * the form back is shown beside its fields, the way a reply's errors are.
 */
function isInvalidReply(response: {readonly type: string}): response is InvalidReply {
    return (
        response.type === "invalid" &&
        Array.isArray((response as {readonly errors?: unknown}).errors)
    );
}

/**
 * Whether every handle is ready to submit, for a save that composes its own
 * payload from several forms. The ones that are not show what holds them back.
 */
export function ready(...handles: readonly SubmittableForm<unknown>[]): boolean {
    let all = true;
    for (const handle of handles) {
        const validated = handle.validated;
        if (!validated.success) {
            handle.setServerErrors(validated.errors, {revision: handle.revision});
            all = false;
        }
    }
    return all;
}

export async function submitForm<TValues, TResponse extends {readonly type: string}>(
    form: SubmittableForm<TValues>,
    submit: (data: TValues) => Promise<TResponse & NoInfer<BindableReply<TResponse>>>,
    options?: SubmitOptions,
): Promise<TResponse | null> {
    if (!ready(form)) {
        return null;
    }
    const validated = form.validated;
    if (!validated.success) {
        return null;
    }
    // The revision the payload was read at: an edit while the request is in
    // flight discards the reply's errors rather than showing them against
    // values the user has already moved on from.
    const revision = form.revision;
    const response = await submit(validated.data);

    if (isInvalidReply(response)) {
        form.setServerErrors(response.errors, {revision, prefix: options?.prefix});
    } else {
        // A reply that is not a refusal retires what the last one said.
        form.setServerErrors([], {revision});
    }
    return response;
}

// ---------------------------------------------------------------------------
// Layout. Data, not a rendering or validation engine.
// ---------------------------------------------------------------------------

type CommonFieldMeta = {
    /** `null` suppresses the visible label, never the accessible one. */
    readonly label?: string | null;
    readonly help?: React.ReactNode;
    /** Visibility only. The value, its validation and its coverage stand. */
    readonly when?: boolean;
};

type FieldEntry<TDefinition> = {
    [TName in FieldName<TDefinition>]: {readonly name: TName} & CommonFieldMeta;
}[FieldName<TDefinition>];

type RowMeta = {
    readonly label?: string;
    readonly help?: React.ReactNode;
};

type RowItem<TDefinition> = FieldName<TDefinition> | FieldEntry<TDefinition>;

type RowList<TDefinition> = readonly [
    RowItem<TDefinition>,
    RowItem<TDefinition>,
    ...RowItem<TDefinition>[],
];

type RowEntry<TDefinition> = {readonly row: RowList<TDefinition>} & RowMeta;

/** A paragraph at its position in layout order. It claims no field. */
type TextEntry = {readonly text: React.ReactNode};

type Placement<TDefinition> =
    | FieldName<TDefinition>
    | FieldEntry<TDefinition>
    | TextEntry
    | RowList<TDefinition>
    | RowEntry<TDefinition>;

type LayoutSection<TDefinition> = {
    readonly name: string;
    readonly label: string;
    readonly description?: string;
    readonly fields: readonly Placement<TDefinition>[];
};

export type LayoutSpec<TDefinition> = {
    readonly sections: readonly LayoutSection<TDefinition>[];
    readonly hidden?: readonly FieldName<TDefinition>[];
};

type IsUnion<T, TAll = T> = T extends TAll
    ? [TAll] extends [T]
        ? false
        : true
    : never;

type UnknownKeys<T, TAllowed extends PropertyKey> =
    Exclude<keyof T, TAllowed> extends infer TKey
        ? TKey extends string
            ? `Layout error: unknown key '${TKey}'`
            : never
        : never;

type NotOneLiteral<T, TAt extends string> = [T] extends [string]
    ? string extends T
        ? `Layout error: ${TAt} must be a literal, not a widened string`
        : true extends IsUnion<T>
          ? `Layout error: ${TAt} must be one name, not a union: ${T}`
          : never
    : never;

type NotATuple<T, TAt extends string> = T extends readonly unknown[]
    ? number extends T["length"]
        ? `Layout error: ${TAt} must be a literal tuple, not a widened array`
        : never
    : `Layout error: ${TAt} must be an array`;

// A row item contributes exactly one name; a text entry contributes none.
type ItemName<TItem> = [TItem] extends [string]
    ? TItem
    : [TItem] extends [{readonly name: infer TName}]
      ? TName
      : never;

type RowNames<TRow> = TRow extends readonly [infer THead, ...infer TTail]
    ? [ItemName<THead>, ...RowNames<TTail>]
    : [];

type PlacementNames<TPlacement> = [TPlacement] extends [{readonly text: unknown}]
    ? []
    : [TPlacement] extends [string]
      ? [TPlacement]
      : [TPlacement] extends [{readonly name: infer TName}]
        ? [TName]
        : [TPlacement] extends [{readonly row: infer TRow}]
          ? RowNames<TRow>
          : [TPlacement] extends [readonly unknown[]]
            ? RowNames<TPlacement>
            : [];

type ListNames<TFields> = TFields extends readonly [infer THead, ...infer TTail]
    ? [...PlacementNames<THead>, ...ListNames<TTail>]
    : [];

type SectionNames<TSections> = TSections extends readonly [infer THead, ...infer TTail]
    ? [
          ...ListNames<THead extends {readonly fields: infer TF} ? TF : never>,
          ...SectionNames<TTail>,
      ]
    : [];

type SectionKeyNames<TSections> = TSections extends readonly [
    infer THead,
    ...infer TTail,
]
    ? [
          THead extends {readonly name: infer TName} ? TName : never,
          ...SectionKeyNames<TTail>,
      ]
    : [];

type AllNames<TSpec> = [
    ...SectionNames<TSpec extends {readonly sections: infer TS} ? TS : never>,
    ...(TSpec extends {readonly hidden: infer THidden}
        ? THidden extends readonly unknown[]
            ? THidden
            : []
        : []),
];

type SequenceErrors<
    TNames extends readonly unknown[],
    TAt extends string,
    TSeen = never,
> = TNames extends readonly [infer THead, ...infer TTail]
    ?
          | NotOneLiteral<THead, TAt>
          | (THead extends TSeen
                ? `Layout error: ${TAt & string} '${THead & string}' appears more than once`
                : never)
          | SequenceErrors<TTail, TAt, TSeen | THead>
    : never;

type FieldMetaKeys = "name" | keyof CommonFieldMeta;
type RowMetaKeys = "row" | keyof RowMeta;

type RowItemKeyErrors<TItem> = [TItem] extends [string]
    ? never
    : UnknownKeys<TItem, FieldMetaKeys>;

type RowItemsKeyErrors<TRow> = TRow extends readonly [infer THead, ...infer TTail]
    ? RowItemKeyErrors<THead> | RowItemsKeyErrors<TTail>
    : never;

type PlacementKeyErrors<TPlacement> = [TPlacement] extends [string]
    ? never
    : [TPlacement] extends [{readonly text: unknown}]
      ? UnknownKeys<TPlacement, "text">
      : [TPlacement] extends [{readonly name: unknown}]
        ? UnknownKeys<TPlacement, FieldMetaKeys>
        : [TPlacement] extends [{readonly row: infer TRow}]
          ? UnknownKeys<TPlacement, RowMetaKeys> | RowItemsKeyErrors<TRow>
          : [TPlacement] extends [readonly unknown[]]
            ? NotATuple<TPlacement, "a row"> | RowItemsKeyErrors<TPlacement>
            : never;

type ListErrors<TFields> = TFields extends readonly [infer THead, ...infer TTail]
    ?
          | (true extends IsUnion<THead>
                ? "Layout error: one entry must be one shape, not a union"
                : never)
          | PlacementKeyErrors<THead>
          | ListErrors<TTail>
    : never;

type SectionsErrors<TDefinition, TSections> = TSections extends readonly [
    infer THead,
    ...infer TTail,
]
    ?
          | UnknownKeys<THead, keyof LayoutSection<TDefinition>>
          | NotATuple<
                THead extends {readonly fields: infer TF} ? TF : never,
                "a section's fields"
            >
          | ListErrors<THead extends {readonly fields: infer TF} ? TF : never>
          | SectionsErrors<TDefinition, TTail>
    : never;

type MissingFields<TDefinition, TSpec> =
    Exclude<FieldName<TDefinition>, AllNames<TSpec>[number]> extends infer TName
        ? TName extends string
            ? `Layout error: field '${TName}' is in no section and not hidden`
            : never
        : never;

type UnknownPlacedFields<TDefinition, TSpec> =
    Exclude<
        AllNames<TSpec>[number] & string,
        FieldName<TDefinition>
    > extends infer TName
        ? TName extends string
            ? `Layout error: '${TName}' is not a field of this form`
            : never
        : never;

type LayoutErrors<TDefinition, TSpec> =
    | UnknownKeys<TSpec, keyof LayoutSpec<TDefinition>>
    | NotATuple<TSpec extends {readonly sections: infer TS} ? TS : never, "sections">
    // Without this a `("source" | "upc")[]` hidden list proves coverage for
    // both names at once and can never be caught placing one of them twice:
    // every element of it is a legal name, so only its shape refuses it.
    | (TSpec extends {readonly hidden: infer THidden}
          ? NotATuple<THidden, "hidden">
          : never)
    | SectionsErrors<
          TDefinition,
          TSpec extends {readonly sections: infer TS} ? TS : never
      >
    | SequenceErrors<
          SectionKeyNames<TSpec extends {readonly sections: infer TS} ? TS : never>,
          "section name"
      >
    | SequenceErrors<AllNames<TSpec>, "field">
    | UnknownPlacedFields<TDefinition, TSpec>
    | MissingFields<TDefinition, TSpec>;

// Success collapses to `{}`; every failure adds a required property whose NAME
// is the diagnostic, so the error reads back as the rule that was broken.
type Check<TErrors> = {readonly [TMessage in TErrors & string]: never};

type MetaLabel<TMeta, TDefault> = TMeta extends {readonly label: infer TLabel}
    ? TLabel
    : TDefault;

type LayoutFieldItem<TDefinition, TName, TMeta> = {
    readonly type: "field";
    readonly name: TName;
    readonly field: TName extends FieldName<TDefinition>
        ? FieldOf<TDefinition, TName>
        : never;
    readonly label: MetaLabel<TMeta, string>;
    readonly help: TMeta extends {readonly help: infer THelp} ? THelp : null;
    readonly when: TMeta extends {readonly when: infer TWhen} ? TWhen : true;
};

type LayoutRowItems<TDefinition, TRow> = TRow extends readonly [
    infer THead,
    ...infer TTail,
]
    ? [
          THead extends string
              ? LayoutFieldItem<TDefinition, THead, unknown>
              : THead extends {readonly name: infer TName}
                ? LayoutFieldItem<TDefinition, TName, THead>
                : never,
          ...LayoutRowItems<TDefinition, TTail>,
      ]
    : [];

type LayoutItem<TDefinition, TPlacement> = [TPlacement] extends [
    {readonly text: infer TText},
]
    ? {readonly type: "text"; readonly text: TText}
    : [TPlacement] extends [string]
      ? LayoutFieldItem<TDefinition, TPlacement, unknown>
      : [TPlacement] extends [{readonly name: infer TName}]
        ? LayoutFieldItem<TDefinition, TName, TPlacement>
        : [TPlacement] extends [{readonly row: infer TRow}]
          ? {
                readonly type: "row";
                readonly label: MetaLabel<TPlacement, null>;
                readonly help: TPlacement extends {readonly help: infer THelp}
                    ? THelp
                    : null;
                readonly fields: LayoutRowItems<TDefinition, TRow>;
            }
          : [TPlacement] extends [readonly unknown[]]
            ? {
                  readonly type: "row";
                  readonly label: null;
                  readonly help: null;
                  readonly fields: LayoutRowItems<TDefinition, TPlacement>;
              }
            : never;

type LayoutItems<TDefinition, TFields> = TFields extends readonly [
    infer THead,
    ...infer TTail,
]
    ? [LayoutItem<TDefinition, THead>, ...LayoutItems<TDefinition, TTail>]
    : [];

declare const sectionOf: unique symbol;

/**
 * Invariant in the definition, so two forms sharing a field name but not its
 * kind are still told apart. The symbol is not constructible outside `layout`,
 * so an object of the right shape is not a section.
 */
export type SectionBrand<TDefinition> = {
    readonly [sectionOf]: (definition: TDefinition) => TDefinition;
};

export type LayoutSectionHandle<TDefinition, TSection> = TSection extends {
    readonly name: infer TName;
    readonly label: infer TLabel;
    readonly fields: infer TFields;
}
    ? SectionBrand<TDefinition> & {
          readonly name: TName;
          readonly label: TLabel;
          readonly description: TSection extends {readonly description: infer TD}
              ? TD
              : null;
          /** Derived from the fields this section places, never from readiness. */
          readonly hasErrors: boolean;
          readonly items: LayoutItems<TDefinition, TFields>;
          readonly fieldNames: ListNames<TFields>;
      }
    : never;

type LayoutSections<TDefinition, TSections> = TSections extends readonly [
    infer THead,
    ...infer TTail,
]
    ? [LayoutSectionHandle<TDefinition, THead>, ...LayoutSections<TDefinition, TTail>]
    : [];

export type LayoutHandle<TDefinition, TSpec> = {
    readonly sections: LayoutSections<
        TDefinition,
        TSpec extends {readonly sections: infer TS} ? TS : never
    >;
    readonly byName: {
        readonly [
            TSection in LayoutSections<
                TDefinition,
                TSpec extends {readonly sections: infer TS} ? TS : never
            >[number] as TSection extends {readonly name: infer TName extends string}
                ? TName
                : never
        ]: TSection;
    };
    readonly hidden: TSpec extends {readonly hidden: infer THidden}
        ? THidden
        : readonly [];
};

type RuntimeFieldItem = {
    readonly type: "field";
    readonly name: string;
    readonly field: RuntimeField;
    readonly label: string | null;
    readonly help: React.ReactNode;
    readonly when: boolean;
};

type RuntimeItem =
    | RuntimeFieldItem
    | {readonly type: "text"; readonly text: React.ReactNode}
    | {
          readonly type: "row";
          readonly label: string | null;
          readonly help: React.ReactNode;
          readonly fields: readonly RuntimeFieldItem[];
      };

type PlacementInput =
    | string
    | readonly PlacementInput[]
    | {
          readonly name?: string;
          readonly row?: readonly PlacementInput[];
          readonly text?: React.ReactNode;
          readonly label?: string | null;
          readonly help?: React.ReactNode;
          readonly when?: boolean;
      };

function layoutField(
    fields: RuntimeFields,
    placement: PlacementInput,
): RuntimeFieldItem {
    const meta = typeof placement === "string" ? {name: placement} : placement;
    const name = (meta as {readonly name?: string}).name ?? "";
    // A deleted Django field still has a place in the layout; it renders
    // nothing rather than taking the layout down.
    const field: RuntimeField = fields[name] ?? {kind: "text", name};
    const stated = meta as {
        readonly label?: string | null;
        readonly help?: React.ReactNode;
        readonly when?: boolean;
    };
    return {
        type: "field",
        name,
        field,
        label: "label" in stated ? (stated.label ?? null) : (field.label ?? name),
        help: stated.help ?? null,
        when: stated.when ?? true,
    };
}

function layoutItem(fields: RuntimeFields, placement: PlacementInput): RuntimeItem {
    if (Array.isArray(placement)) {
        return {
            type: "row",
            label: null,
            help: null,
            fields: (placement as readonly PlacementInput[]).map((item) =>
                layoutField(fields, item),
            ),
        };
    }
    if (typeof placement === "object") {
        const entry = placement as {
            readonly text?: React.ReactNode;
            readonly row?: readonly PlacementInput[];
            readonly label?: string | null;
            readonly help?: React.ReactNode;
        };
        if ("text" in entry) {
            return {type: "text", text: entry.text};
        }
        if (entry.row !== undefined) {
            return {
                type: "row",
                label: entry.label ?? null,
                help: entry.help ?? null,
                fields: entry.row.map((item) => layoutField(fields, item)),
            };
        }
    }
    return layoutField(fields, placement);
}

function itemNames(item: RuntimeItem): readonly string[] {
    if (item.type === "field") {
        return [item.name];
    }
    // A text entry claims no field, so it carries no error either.
    return item.type === "row" ? item.fields.map((field) => field.name) : [];
}

export function layout<
    TDefinition extends AnyDefinition,
    const TSpec extends LayoutSpec<TDefinition>,
>(
    form: {readonly definition: TDefinition},
    spec: TSpec & NoInfer<Check<LayoutErrors<TDefinition, TSpec>>>,
): LayoutHandle<TDefinition, TSpec> {
    const fields = runtimeFields(form.definition);
    // Every callsite passes the whole handle; only the definition is stated,
    // and `hasErrors` is read here rather than captured, so a section never
    // answers from an older render's errors.
    const errors =
        (form as {readonly errors?: Readonly<Record<string, FieldErrors>>}).errors ??
        {};
    const stated = spec as unknown as {
        readonly sections: readonly {
            readonly name: string;
            readonly label: string;
            readonly description?: string;
            readonly fields: readonly PlacementInput[];
        }[];
        readonly hidden?: readonly string[];
    };

    const sections = stated.sections.map((section) => {
        const items = section.fields.map((placement) => layoutItem(fields, placement));
        const fieldNames = items.flatMap((item) => itemNames(item));
        return {
            name: section.name,
            label: section.label,
            description: section.description ?? null,
            hasErrors: fieldNames.some((name) => (errors[name] ?? null) !== null),
            items,
            fieldNames,
        };
    });

    const byName: Record<string, unknown> = {};
    for (const section of sections) {
        byName[section.name] = section;
    }

    return {
        sections,
        byName,
        hidden: stated.hidden ?? [],
    } as unknown as LayoutHandle<TDefinition, TSpec>;
}

// ---------------------------------------------------------------------------
// Rendering.
// ---------------------------------------------------------------------------

/**
 * The ids a group layout mints for ONE rendered instance. Never derived from
 * the field name: the same field may render in two places at once.
 */
export type Associations = {
    readonly id?: string;
    readonly "aria-label"?: string;
    readonly "aria-labelledby"?: string;
    readonly "aria-describedby"?: string;
    readonly "aria-invalid"?: true;
};

/**
 * A custom control over an enum field. `NoInfer` on the extras is what keeps
 * the domain the FIELD states: without it, a `descriptions` map would widen
 * `TEnum` to whatever keys the caller happened to write.
 *
 * The field slot names both spellings of the domain deliberately. The setter
 * is contravariant, so a control declared for `TEnum | null` cannot hold a
 * field with no blank — and a control that named only `TEnum` could never hold
 * one that has a blank. Radio controls stay restricted to text domains; a
 * numeric or boolean select is refused here and goes through `Widget`.
 */
export type EnumFieldHandle<TEnum extends string> = Omit<
    FieldHandle<"select", TEnum | null>,
    "value" | "options" | "disabledOptions" | "onChange"
> & {
    readonly value: TEnum | null;
    readonly options: readonly (readonly [TEnum | null, string])[];
    readonly disabledOptions: readonly (TEnum | null)[];
    /**
     * A radio group selects a member; it never clears to a blank. Narrowing
     * the setter here is what lets one control serve a field that has a blank
     * member and one that does not, without the setter going bivariant.
     */
    readonly onChange: (value: TEnum) => void;
};

export type EnumWidgetProps<TEnum extends string, TExtra = unknown> = {
    readonly field: EnumFieldHandle<TEnum>;
    readonly className?: string;
    readonly readOnly?: boolean;
} & Associations &
    NoInfer<TExtra>;

/** A component with no extras of its own. Its `props` slot is optional. */
type NoExtras = Record<string, never>;

type ControlProps = {
    readonly className?: string;
    readonly placeholder?: string;
    readonly readOnly?: boolean;
} & Associations;

/**
 * A select's entry is GENERIC over its domain, which may be strings, numbers
 * or booleans: an entry fixed at `string | null` would either refuse a numeric
 * select or, with a bivariant setter, let its control write text into one.
 * Every other kind's entry takes the kind's registered handle, which reads what
 * the kind admits and writes only what its control can produce.
 */
export type WidgetRegistry = {
    readonly [TKind in FieldKind]?: TKind extends "select"
        ? <TValue extends AnyValue>(
              props: {readonly field: FieldHandle<"select", TValue>} & ControlProps,
          ) => React.ReactNode
        : (
              props: {
                  readonly field: RegisteredFieldHandle<Exclude<TKind, "select">>;
              } & ControlProps,
          ) => React.ReactNode;
};

type WidgetOwnProps<THandle> = {
    readonly field: THandle;
    readonly className?: string;
    /** Both override the definition for THIS rendering only. */
    readonly placeholder?: string;
    readonly readOnly?: boolean;
} & Associations;

// The component and its props stay ONE typed relation: `TExtra` is inferred
// from `props`, and the component is then checked against a concrete target,
// which is what instantiates a generic widget's domain from the field. The
// component receives the same presentation props `Widget` accepts, because
// `Widget` is what renders it.
type WidgetComponentProps<THandle, TExtra extends object> = WidgetOwnProps<THandle> & {
    readonly component: (
        props: Omit<WidgetOwnProps<THandle>, "field"> & {
            readonly field: THandle;
        } & NoInfer<TExtra>,
    ) => React.ReactNode;
} & (NoExtras extends TExtra ? {readonly props?: TExtra} : {readonly props: TExtra});

export type WidgetProps<THandle, TExtra extends object> =
    | WidgetOwnProps<THandle>
    | WidgetComponentProps<THandle, TExtra>;

/**
 * Renders the control and nothing else: no label, no help, no error node.
 *
 * The handle is taken by its kind and value rather than as one existential
 * union, so a control the caller names is checked against the field's own
 * domain.
 */
/**
 * A control's own editing text.
 *
 * The form learns only whether the text reads, which is what keeps an
 * unreadable buffer out of a payload without the library owning a parser per
 * control. Text stays on screen while it still describes the field's value, so
 * a save that republishes the same value does not overwrite an active buffer.
 *
 * Unmounting is not an edit: a control that goes behind a tab leaves its
 * unreadable text with the form, and reads it back from the binding when it
 * returns. A value stated from OUTSIDE the control discards it.
 *
 * Two controls over one field need no arbitration: only the one being typed
 * into holds text, and the other's own text stops describing the field's value
 * the moment it moves, so it falls back to showing that value.
 */
export function useBuffer<TValue extends AnyValue>(
    field: {
        readonly value: TValue;
        readonly onChange: (value: TValue) => void;
    },
    parse: {
        readonly read: (text: string) => {readonly value: AnyValue} | null;
        readonly show: (value: TValue) => string;
    },
): {readonly text: string; readonly onText: (text: string) => void} {
    const binding = bindingOf(field);
    const epoch = binding?.epoch ?? 0;
    // Seeded from the binding, so a bound POST's unreadable text is on screen
    // from the first render, SSR included, rather than the value it replaced.
    const [held, setHeld] = React.useState<string | null>(() => binding?.draft ?? null);
    const [seen, setSeen] = React.useState(epoch);

    const cleared = epoch !== seen;
    if (cleared) {
        setSeen(epoch);
        setHeld(null);
    }
    // `held` is this render's copy, which the adjustment above has not reached.
    const current = cleared ? null : held;

    const read = current === null ? null : parse.read(current);
    const text =
        current === null
            ? parse.show(field.value)
            : read === null || read.value === field.value
              ? current
              : parse.show(field.value);

    return {
        text,
        onText: (next: string) => {
            setHeld(next);
            const outcome = parse.read(next);
            if (binding === null) {
                if (outcome !== null) {
                    field.onChange(outcome.value as TValue);
                }
                return;
            }
            binding.report(outcome === null ? next : null);
            if (outcome !== null) {
                // The binding's own path, not `onChange`: this write must not
                // restate the field and clear the text it came from.
                binding.publish(outcome.value);
            }
        },
    };
}

// ---------------------------------------------------------------------------
// Builtin controls. Plain DOM and no styling: an app registers its own.
// ---------------------------------------------------------------------------

type BuiltinProps = {readonly field: FieldHandle<FieldKind, AnyValue>} & ControlProps;

/** The handle read back as the field definition the readers above take. */
function handleField(field: FieldHandle<FieldKind, AnyValue>): RuntimeField {
    return {kind: field.kind, name: field.name, options: field.options};
}

function associationsOf(props: BuiltinProps): Associations {
    return {
        id: props.id,
        "aria-label": props["aria-label"],
        "aria-labelledby": props["aria-labelledby"],
        "aria-describedby": props["aria-describedby"],
        "aria-invalid": props["aria-invalid"],
    };
}

const INPUT_TYPES: Readonly<Partial<Record<FieldKind, string>>> = {
    password: "password",
    email: "email",
    date: "date",
    datetime: "datetime-local",
    range: "range",
};

function InputControl(props: BuiltinProps) {
    const {field} = props;
    const buffer = useBuffer(field, {
        read: (text) => readText(handleField(field), text),
        show: (value) => (value == null ? "" : String(value)),
    });

    return (
        <input
            {...associationsOf(props)}
            type={INPUT_TYPES[field.kind] ?? "text"}
            inputMode={
                field.kind === "number" || field.kind === "duration"
                    ? "numeric"
                    : DECIMAL_KINDS.includes(field.kind)
                      ? "decimal"
                      : undefined
            }
            name={field.name}
            className={props.className}
            placeholder={props.placeholder ?? field.placeholder}
            readOnly={props.readOnly ?? field.readOnly}
            value={buffer.text}
            onChange={(event) => {
                buffer.onText(event.target.value);
            }}
        />
    );
}

function TextAreaControl(props: BuiltinProps) {
    const {field} = props;
    return (
        <textarea
            {...associationsOf(props)}
            name={field.name}
            className={props.className}
            placeholder={props.placeholder ?? field.placeholder}
            readOnly={props.readOnly ?? field.readOnly}
            value={typeof field.value === "string" ? field.value : ""}
            onChange={(event) => {
                field.onChange(event.target.value);
            }}
        />
    );
}

function BooleanControl(props: BuiltinProps) {
    const {field} = props;
    return (
        <input
            {...associationsOf(props)}
            type="checkbox"
            name={field.name}
            className={props.className}
            disabled={props.readOnly ?? field.readOnly}
            checked={field.value === true}
            onChange={(event) => {
                field.onChange(event.target.checked);
            }}
        />
    );
}

function SelectControl(props: BuiltinProps) {
    const {field} = props;
    return (
        <select
            {...associationsOf(props)}
            name={field.name}
            className={props.className}
            disabled={props.readOnly ?? field.readOnly}
            value={optionText(field.value)}
            onChange={(event) => {
                const read = readText(handleField(field), event.target.value);
                field.onChange(read === null ? null : read.value);
            }}
        >
            {field.options.map(([value, label]) => (
                <option
                    key={optionText(value)}
                    value={optionText(value)}
                    disabled={field.disabledOptions.includes(value)}
                >
                    {label}
                </option>
            ))}
        </select>
    );
}

const MONTHS = Array.from({length: 12}, (_unused, index) => index + 1);
const DAYS = Array.from({length: 31}, (_unused, index) => index + 1);
const YEARS = Array.from(
    {length: 120},
    (_unused, index) => new Date().getFullYear() - index,
);

/**
 * The compound date widget: one POST name per segment, from `parts`.
 *
 * A partial selection is kept exactly as it was chosen — Django would write
 * zeros over the blanks — and leaves the field unready until it is a date.
 */
function DatePartsControl(props: BuiltinProps) {
    const {field} = props;
    const binding = bindingOf(field);
    const held = binding?.draft ?? (typeof field.value === "string" ? field.value : "");
    const [year = "", month = "", day = ""] = held.split("-");
    const parts = field.parts;
    // The options carry Django's own values ("1".."12", "1".."31"), so a native
    // POST and a test that picks "1" read the same on either renderer; the
    // text held is ISO, so the segments pad on the way in and unpad on the
    // way out.
    const unpadded = (segment: string) =>
        segment === "" ? "" : String(Number(segment));
    const padded = (segment: string) =>
        segment === "" ? "" : segment.padStart(2, "0");

    // Its own writes go through the binding for the same reason a buffer's do:
    // a control does not restate the field it is itself being typed into. The
    // segments are read back from `binding.draft` each render, so a value
    // stated from outside still lands here with nothing to clear.
    const state = (value: AnyValue) => {
        if (binding === null) {
            field.onChange(value);
        } else {
            binding.publish(value);
        }
    };

    const publish = (segments: {
        readonly year: string;
        readonly month: string;
        readonly day: string;
    }) => {
        if (segments.year === "" && segments.month === "" && segments.day === "") {
            binding?.report(null);
            state(null);
            return;
        }
        const text = `${segments.year}-${padded(segments.month)}-${padded(segments.day)}`;
        const read = readText(handleField(field), text);
        if (read === null) {
            binding?.report(text);
        } else {
            binding?.report(null);
            state(read.value);
        }
    };

    const segment = (
        name: string | undefined,
        value: string,
        options: readonly (readonly [string, string])[],
        onSelect: (next: string) => void,
        label: string,
    ) => (
        <select
            name={name}
            aria-label={label}
            className={props.className}
            disabled={props.readOnly ?? field.readOnly}
            value={value}
            onChange={(event) => {
                onSelect(event.target.value);
            }}
        >
            <option value="">{label}</option>
            {options.map(([optionValue, optionLabel]) => (
                <option key={optionValue} value={optionValue}>
                    {optionLabel}
                </option>
            ))}
        </select>
    );
    const years = (parts?.years ?? YEARS).map(
        (option) => [String(option), String(option)] as const,
    );
    const months = MONTHS.map(
        (option, index) =>
            [String(option), parts?.months[index] ?? String(option)] as const,
    );
    const days = DAYS.map((option) => [String(option), String(option)] as const);
    const [yearLabel = "Year", monthLabel = "Month", dayLabel = "Day"] =
        parts?.empty ?? [];

    // `className` styles each segment; the row is the app's, on the data hook.
    const segments = {
        year: segment(
            parts?.year,
            year,
            years,
            (next) => {
                publish({year: next, month, day});
            },
            yearLabel,
        ),
        month: segment(
            parts?.month,
            unpadded(month),
            months,
            (next) => {
                publish({year, month: next, day});
            },
            monthLabel,
        ),
        day: segment(
            parts?.day,
            unpadded(day),
            days,
            (next) => {
                publish({year, month, day: next});
            },
            dayLabel,
        ),
    };
    const order = parts?.order ?? (["year", "month", "day"] as const);

    return (
        <span {...associationsOf(props)} data-date-parts="">
            {order.map((name) => (
                <React.Fragment key={name}>{segments[name]}</React.Fragment>
            ))}
        </span>
    );
}

function BuiltinWidget(props: BuiltinProps): React.ReactElement {
    const {field} = props;
    if (field.widget === "select-date" && field.parts !== null) {
        return <DatePartsControl {...props} />;
    }
    if (field.kind === "boolean") {
        return <BooleanControl {...props} />;
    }
    if (field.kind === "select") {
        return <SelectControl {...props} />;
    }
    if (field.kind === "textarea" || field.kind === "markdown") {
        return <TextAreaControl {...props} />;
    }
    return <InputControl {...props} />;
}

type AnyControl = React.FunctionComponent<Record<string, unknown>>;

/**
 * One dispatch for every path into a control: the component a callsite named,
 * else the app's registered control for the kind, else the builtin.
 */
function dispatchWidget(
    props: Readonly<Record<string, unknown>>,
    registry: WidgetRegistry | null,
): React.ReactElement {
    const {
        field,
        component,
        props: extra,
        ...rest
    } = props as {
        readonly field: FieldHandle<FieldKind, AnyValue>;
        readonly component?: unknown;
        readonly props?: Readonly<Record<string, unknown>>;
    } & Readonly<Record<string, unknown>>;

    // `placeholder` and `readOnly` override the definition for THIS rendering,
    // so they travel on the handle: a registered control reads them off the
    // field it is given rather than from a second channel.
    const overrides: Record<string, unknown> = {};
    if (rest.readOnly !== undefined) {
        overrides.readOnly = rest.readOnly;
    }
    if (rest.placeholder !== undefined) {
        overrides.placeholder = rest.placeholder;
    }
    const bound =
        Object.keys(overrides).length > 0
            ? {...field, ...overrides}
            : (field as object);

    if (component !== undefined) {
        return React.createElement(component as AnyControl, {
            ...rest,
            ...extra,
            field: bound,
        });
    }
    const registered = registry?.[field.kind];
    if (registered !== undefined) {
        return React.createElement(registered as unknown as AnyControl, {
            ...rest,
            field: bound,
        });
    }
    return React.createElement(BuiltinWidget as unknown as AnyControl, {
        ...rest,
        field: bound,
    });
}

export function Widget<
    TKind extends FieldKind,
    TValue extends AdmissibleValue<TKind>,
    TExtra extends object = NoExtras,
>(props: WidgetProps<FieldHandle<TKind, TValue>, TExtra>): React.ReactElement {
    return dispatchWidget(props, null);
}

export type FieldComponentProps<
    THandle,
    TExtra extends object,
    TLayoutProps,
> = WidgetProps<THandle, TExtra> & TLayoutProps;

/** One entry a section renders, after its metadata is resolved. */
export type RenderedField<TDefinition> = {
    readonly type: "field";
    readonly name: FieldName<TDefinition>;
    readonly field: FieldDefinition<AnyValue>;
    readonly label: string | null;
    readonly help: React.ReactNode;
    readonly when: boolean;
};

export type RenderedItem<TDefinition> =
    | RenderedField<TDefinition>
    | {readonly type: "text"; readonly text: React.ReactNode}
    | {
          readonly type: "row";
          readonly label: string | null;
          readonly help: React.ReactNode;
          readonly fields: readonly RenderedField<TDefinition>[];
      };

/**
 * A section as `layout` returns it, tied to the form that produced it. The
 * brand is invariant, so a section built from another definition is refused
 * even when its field names happen to match, and a fabricated object is
 * refused because it cannot carry the brand.
 */
export type LayoutSectionFor<TDefinition> = SectionBrand<TDefinition> & {
    readonly name: string;
    readonly label: string;
    readonly hasErrors: boolean;
    readonly items: readonly RenderedItem<TDefinition>[];
    readonly fieldNames: readonly FieldName<TDefinition>[];
};

export type FieldsProps<TDefinition, TValues, TLayoutProps> = {
    readonly form: FormHandle<TDefinition, TValues>;
    readonly readOnly?: boolean;
} & (
    | {
          readonly fields?: readonly FieldName<TDefinition>[];
          readonly exclude?: readonly FieldName<TDefinition>[];
          readonly layout?: undefined;
      }
    | {
          // NoInfer: the section never decides which form this is; the form
          // does, and the section is then checked against it.
          readonly layout: NoInfer<LayoutSectionFor<TDefinition>>;
          readonly fields?: undefined;
          readonly exclude?: undefined;
      }
) &
    TLayoutProps;

/**
 * The whole form's contents, like Django's as_div: non-field and hidden-field
 * errors once, then the field groups. It emits no form element, no CSRF input
 * and no submit button.
 */
export type FormProps<TDefinition, TValues, TLayoutProps> = {
    readonly form: FormHandle<TDefinition, TValues>;
    readonly readOnly?: boolean;
} & TLayoutProps;

export type FieldsComponent<TLayoutProps extends object> = {
    <TDefinition extends AnyDefinition, TValues>(
        props: FieldsProps<TDefinition, TValues, TLayoutProps>,
    ): React.ReactElement;
    readonly Field: <
        TKind extends FieldKind,
        TValue extends AdmissibleValue<TKind>,
        TExtra extends object = NoExtras,
    >(
        props: FieldComponentProps<FieldHandle<TKind, TValue>, TExtra, TLayoutProps>,
    ) => React.ReactElement;
    readonly Form: <TDefinition extends AnyDefinition, TValues>(
        props: FormProps<TDefinition, TValues, TLayoutProps>,
    ) => React.ReactElement;
};

/**
 * The app states the label, help and error DOM for one field. `renderWidget`
 * arrives prebound with the component and props the callsite named, so the
 * dispatch and its type checking stay here and the app holds a typed slot.
 *
 * The field arrives without its setter: a layout reads the label, the help and
 * the errors, and hands the control back to `renderWidget`, which still holds
 * the field's own domain.
 */
export type FieldRenderer<TLayoutProps extends object> = (
    field: AnyFieldHandle,
    props: TLayoutProps,
    renderWidget: (associations: Associations) => React.ReactNode,
) => React.ReactNode;

export type Renderers = {
    readonly Widget: typeof Widget;
    readonly createRenderer: <TLayoutProps extends object>(
        render: FieldRenderer<TLayoutProps>,
    ) => FieldsComponent<TLayoutProps>;
};

// Everything a control is given. Anything else a callsite passes belongs to
// the layout, which is the app's own shape.
const CONTROL_KEYS: readonly string[] = [
    "field",
    "component",
    "props",
    "className",
    "placeholder",
    "readOnly",
    "id",
    "aria-label",
    "aria-labelledby",
    "aria-describedby",
    "aria-invalid",
];

type AnyForm = {
    readonly definition: AnyDefinition;
    readonly order: readonly string[];
    readonly fields: Readonly<Record<string, object | undefined>>;
    readonly errors: Readonly<Record<string, FieldErrors>>;
    readonly nonFieldErrors: readonly string[] | null;
};

function splitRenderProps(props: Readonly<Record<string, unknown>>): {
    readonly control: Record<string, unknown>;
    readonly layout: Record<string, unknown>;
} {
    const control: Record<string, unknown> = {};
    const layout: Record<string, unknown> = {};
    for (const key of Object.keys(props)) {
        if (CONTROL_KEYS.includes(key)) {
            control[key] = props[key];
        } else {
            layout[key] = props[key];
        }
    }
    // Presentation for both halves: a layout may place it on its wrapper and
    // a control on its input.
    if ("className" in props) {
        layout.className = props.className;
    }
    return {control, layout};
}

function hiddenNames(form: AnyForm): readonly string[] {
    return extrasOf(form.definition).hidden_fields ?? [];
}

/** Non-field errors, and the errors of fields nothing visible can show. */
function FormErrorList(props: {readonly form: AnyForm}): React.ReactElement {
    const {form} = props;
    const messages = [
        ...(form.nonFieldErrors ?? []),
        ...hiddenNames(form).flatMap((name) => form.errors[name] ?? []),
    ];
    if (messages.length === 0) {
        return <></>;
    }
    return (
        <ul role="alert">
            {messages.map((message, index) => (
                <li key={index}>{message}</li>
            ))}
        </ul>
    );
}

function buildRenderer<TLayoutProps extends object>(
    render: FieldRenderer<TLayoutProps>,
    registry: WidgetRegistry | null,
): FieldsComponent<TLayoutProps> {
    const renderOne = (
        field: object,
        control: Readonly<Record<string, unknown>>,
        layout: Readonly<Record<string, unknown>>,
    ): React.ReactNode =>
        render(field as AnyFieldHandle, layout as TLayoutProps, (associations) =>
            dispatchWidget({...control, ...associations, field}, registry),
        );

    const renderField = (
        form: AnyForm,
        name: string,
        meta: {readonly label: string | null; readonly help: React.ReactNode} | null,
        split: ReturnType<typeof splitRenderProps>,
    ): React.ReactNode => {
        const handle = form.fields[name];
        // A Django view may delete a field the layout still names.
        if (handle === undefined) {
            return null;
        }
        const layout =
            meta === null
                ? split.layout
                : {...split.layout, label: meta.label, help: meta.help};
        return (
            <React.Fragment key={name}>
                {renderOne(handle, split.control, layout)}
            </React.Fragment>
        );
    };

    const Field = (props: Readonly<Record<string, unknown>>) => {
        const split = splitRenderProps(props);
        return <>{renderOne(props.field as object, split.control, split.layout)}</>;
    };

    const Fields = (props: Readonly<Record<string, unknown>>) => {
        const {
            form,
            fields: selected,
            exclude,
            layout: section,
            ...rest
        } = props as {
            readonly form: AnyForm;
            readonly fields?: readonly string[];
            readonly exclude?: readonly string[];
            readonly layout?: {
                readonly description?: string | null;
                readonly items: readonly RuntimeItem[];
            };
        } & Readonly<Record<string, unknown>>;
        const split = splitRenderProps(rest);

        if (section !== undefined) {
            return (
                <>
                    {section.description != null && <p>{section.description}</p>}
                    {section.items.map((item, index) => {
                        if (item.type === "text") {
                            return <p key={index}>{item.text}</p>;
                        }
                        if (item.type === "row") {
                            const visible = item.fields.filter((entry) => entry.when);
                            if (visible.length === 0) {
                                return null;
                            }
                            // Data hooks rather than classes or inline style:
                            // the joined look is the app's, and the app has
                            // CSS of its own to hang on them.
                            return (
                                <fieldset key={index} data-form-row="">
                                    {item.label != null && (
                                        <legend data-form-row-label="">
                                            {item.label}
                                        </legend>
                                    )}
                                    {item.help != null && (
                                        <p data-form-row-help="">{item.help}</p>
                                    )}
                                    <div data-form-row-fields="">
                                        {visible.map((entry) => (
                                            <div
                                                key={entry.name}
                                                data-form-row-field=""
                                            >
                                                {renderField(
                                                    form,
                                                    entry.name,
                                                    entry,
                                                    split,
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </fieldset>
                            );
                        }
                        return item.when
                            ? renderField(form, item.name, item, split)
                            : null;
                    })}
                </>
            );
        }

        const names = (selected ?? form.order).filter(
            (name) => !(exclude ?? []).includes(name),
        );
        return <>{names.map((name) => renderField(form, name, null, split))}</>;
    };

    const Form = (props: Readonly<Record<string, unknown>>) => {
        const {form, ...rest} = props as {readonly form: AnyForm} & Readonly<
            Record<string, unknown>
        >;
        const split = splitRenderProps(rest);
        const hidden = hiddenNames(form);
        return (
            <>
                <FormErrorList form={form} />
                {form.order.map((name) => {
                    const handle = form.fields[name] as
                        | FieldHandle<FieldKind, AnyValue>
                        | undefined;
                    if (handle === undefined) {
                        return null;
                    }
                    return hidden.includes(name) ? (
                        <input
                            key={name}
                            type="hidden"
                            name={handle.name}
                            value={handle.value == null ? "" : String(handle.value)}
                        />
                    ) : (
                        renderField(form, name, null, split)
                    );
                })}
            </>
        );
    };

    return Object.assign(Fields, {Field, Form});
}

/** Builtins stand on their own; a registry only overrides a kind. */
export function createRenderers(options: {
    readonly widgets: WidgetRegistry;
}): Renderers {
    const registry = options.widgets;

    function Bound<
        TKind extends FieldKind,
        TValue extends AdmissibleValue<TKind>,
        TExtra extends object = NoExtras,
    >(props: WidgetProps<FieldHandle<TKind, TValue>, TExtra>): React.ReactElement {
        return dispatchWidget(props, registry);
    }

    return {
        Widget: Bound,
        createRenderer: (render) => buildRenderer(render, registry),
    };
}

export function createRenderer<TLayoutProps extends object>(
    render: FieldRenderer<TLayoutProps>,
): FieldsComponent<TLayoutProps> {
    return buildRenderer(render, null);
}

type BuiltinLayoutProps = {
    readonly label?: React.ReactNode | false;
    readonly help?: React.ReactNode | false;
};

function BuiltinField(props: {
    readonly field: AnyFieldHandle;
    readonly label?: React.ReactNode | false;
    readonly help?: React.ReactNode | false;
    readonly renderWidget: (associations: Associations) => React.ReactNode;
}): React.ReactElement {
    // Minted per instance: the same field may render in two places at once.
    const generated = React.useId();
    const controlId = `${generated}-control`;
    const helpId = `${generated}-help`;
    const errorId = `${generated}-error`;
    const error = props.field.errors?.[0] ?? null;
    const label = props.label === false ? null : (props.label ?? props.field.label);
    const help = props.help === false ? null : (props.help ?? props.field.helpText);
    const described = [
        help != null ? helpId : null,
        error != null ? errorId : null,
    ].filter((id) => id != null);

    return (
        <div>
            {label != null && <label htmlFor={controlId}>{label}</label>}
            {props.renderWidget({
                id: controlId,
                // A suppressed visible label still names the control.
                "aria-label": label == null ? props.field.label : undefined,
                "aria-describedby":
                    described.length > 0 ? described.join(" ") : undefined,
                "aria-invalid": error != null ? true : undefined,
            })}
            {help != null && <p id={helpId}>{help}</p>}
            {error != null && (
                <p id={errorId} role="alert">
                    {error}
                </p>
            )}
        </div>
    );
}

const BuiltinFields = buildRenderer<BuiltinLayoutProps>(
    (field, props, renderWidget) => (
        <BuiltinField
            field={field}
            label={props.label}
            help={props.help}
            renderWidget={renderWidget}
        />
    ),
    null,
);

/** One field group: the label, the control, the help and the inline errors. */
export function Field<
    TKind extends FieldKind,
    TValue extends AdmissibleValue<TKind>,
    TExtra extends object = NoExtras,
>(
    props: FieldComponentProps<FieldHandle<TKind, TValue>, TExtra, BuiltinLayoutProps>,
): React.ReactElement {
    const Rendered = BuiltinFields.Field;
    return <Rendered {...props} />;
}

/** The named fields, in the order given, with no whole-form summary. */
export function Fields<TDefinition extends AnyDefinition, TValues>(
    props: FieldsProps<TDefinition, TValues, BuiltinLayoutProps>,
): React.ReactElement {
    return <BuiltinFields {...props} />;
}

export function Form<TDefinition extends AnyDefinition, TValues>(
    props: FormProps<TDefinition, TValues, BuiltinLayoutProps>,
): React.ReactElement {
    const Rendered = BuiltinFields.Form;
    return <Rendered {...props} />;
}

/** Non-field errors, explicit at a hand-composed native form. Never in a loop. */
export function FormErrors<TDefinition extends AnyDefinition, TValues>(props: {
    readonly form: FormHandle<TDefinition, TValues>;
}): React.ReactElement {
    return <FormErrorList form={props.form} />;
}
